import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { fetchUsers } from '../../../actions/users';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from '../../Dropdown';

const BatchJobPermissionModal = ({ isOpen, onClose, userToEdit, setUserToEdit }) => {
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    userId: '',
    permissions: []
  });
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // Available permissions for batch jobs
  const permissionOptions = [
    { label: 'View Job Initialisation', value: 'view_batch_jobs_initialisation' },
    { label: 'Trigger Job', value: 'trigger_batch_job' },
    { label: 'View Running Jobs', value: 'view_running_batch_jobs' },
    { label: 'View Scheduled Jobs', value: 'view_scheduled_batch_jobs' },
    { label: 'Schedule Job', value: 'schedule_batch_job' },
    { label: 'Delete Scheduled Job', value: 'delete_scheduled_batch_job' },
    { label: 'Edit Scheduled Job', value: 'edit_scheduled_batch_job' },
  ];

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Get users from Redux store
  const allUsers = useSelector(state => state.users.users || []);
  
  const userOptions = allUsers.map(user => user.name);

  useEffect(() => {
    if (userToEdit) {
      setUserData({
        name: userToEdit.name,
        email: userToEdit.email,
        userId: userToEdit.userId,
        permissions: userToEdit.batchJobPermissions || []
      });
    } else {
      // Reset form when not editing
      setUserData({
        name: '',
        email: '',
        userId: '',
        permissions: []
      });
    }
  }, [userToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (!userData?.userId && !userData?.email) {
        throw new Error('Please select a valid user');
      }
      
      if (userData.permissions.length === 0) {
        throw new Error('Please select at least one permission');
      }
      
      if (userToEdit) {
        // Update existing user permissions
        const response = await fetch(`https://cloudify-prod.wizr.in/api/users/${userToEdit.userId}/batch-job-permissions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            permissions: userData.permissions
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to update user permissions');
        }
      } else {
        // Add permissions to new user
        const response = await fetch(`https://cloudify-prod.wizr.in/api/users/${userData.userId}/batch-job-permissions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            permissions: userData.permissions
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to add user permissions');
        }
      }

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error submitting permissions:', error);
      setError(error.message || 'Failed to save user permissions');
    }
  };

  const resetForm = () => {
    setUserData({
      name: '',
      email: '',
      userId: '',
      permissions: []
    });
    setUserToEdit(null);
    setError(null);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handlePermissionChange = (permission) => {
    const currentPermissions = [...userData.permissions];
    const index = currentPermissions.indexOf(permission);
    
    if (index === -1) {
      currentPermissions.push(permission);
    } else {
      currentPermissions.splice(index, 1);
    }
    
    setUserData({...userData, permissions: currentPermissions});
  };

  const toggleAllPermissions = () => {
    if (userData.permissions.length === permissionOptions.length) {
      // If all permissions are selected, deselect all
      setUserData({...userData, permissions: []});
    } else {
      // Otherwise, select all permissions
      setUserData({...userData, permissions: permissionOptions.map(option => option.value)});
    }
  };

  const handleUserSelect = (selectedOption) => {
    if (!selectedOption) return;
    
    const selectedUser = allUsers.find(user =>  
      user.name === selectedOption
    );
    
    if (selectedUser) {
      setUserData({
        ...userData,
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        userId: selectedUser.userId || ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              {userToEdit ? 'Edit User Permissions' : 'Add User Permissions'}
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!userToEdit && (
                <div>
                  <label className="text-gray-700 text-sm font-medium block mb-1">
                    User
                  </label>
                  <Dropdown
                    items={userOptions}
                    onSelect={handleUserSelect}
                    value={userData.name || userData.email}
                    placeholder="Search users..."
                    allowCustomValues={false}
                  />
                </div>
              )}

              {userToEdit && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="text-sm font-medium text-gray-700">User</div>
                  <div className="text-base font-medium text-gray-900 mt-1">{userToEdit.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{userToEdit.email}</div>
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Permissions
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllPermissions}
                    className="text-indigo-600 text-sm hover:text-indigo-800 focus:outline-none"
                  >
                    {userData.permissions.length === permissionOptions.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="space-y-2 border border-gray-200 rounded-md p-3">
                  {permissionOptions.map((option) => (
                    <div 
                      key={option.value}
                      className="flex items-center justify-between hover:bg-gray-50 rounded-md p-2 transition-colors cursor-pointer"
                      onClick={() => handlePermissionChange(option.value)}
                    >
                      <div>
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={userData.permissions.includes(option.value)}
                        onChange={() => {}}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {userToEdit ? 'Update Permissions' : 'Add Permissions'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchJobPermissionModal;